import React, { useState } from 'react'
import {
  Grid,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core'
import moment from 'moment'

import { Dialog, snackbar } from 'components'
import { ERRORS, STRINGS } from 'consts'
import { useApi } from 'hooks'
import { api } from 'utils'

import { replyToGoogleReview } from '../../googleApi'
import { replyToNativeReview } from '../../api'
import { getReplyTemplates } from '../../replyTemplatesApi'
import { GetReplyTemplatesResponse, ReviewReplyTemplate } from '../../replyTemplateTypes'
import { ReviewRow } from '../../types'

interface ReviewDetailDialogProps {
  review: ReviewRow
  onClose: () => void
  onReplied?: () => void
  canRespond?: boolean
}

const formatDate = (date?: string) => (date ? moment(date).format(STRINGS.DATE_TIME_FORMAT) : '—')

const getUserLabel = (review: ReviewRow): string => {
  if (review.user) {
    return `${review.user.fullname || 'Sin nombre'} (${review.user.email || 'Sin email'})`
  }
  if ((review.source === 'google' || review.source === 'qr') && review.reviewerName) {
    if (review.source === 'qr' && review.qrEmail) {
      return `${review.reviewerName} (${review.qrEmail})`
    }
    return review.reviewerName
  }
  return '—'
}

const getReplyTemplatesResponseGetter = (responseData: GetReplyTemplatesResponse) =>
  responseData?.replyTemplates ?? []

interface ReplyTemplateSelectProps {
  templates: ReviewReplyTemplate[]
  selectedTemplateId: number | ''
  labelId: string
  onSelect: (event: React.ChangeEvent<{ value: unknown }>) => void
}

const ReplyTemplateSelect: React.FC<ReplyTemplateSelectProps> = ({
  templates,
  selectedTemplateId,
  labelId,
  onSelect,
}) => {
  if (templates.length === 0) return null

  return (
    <FormControl fullWidth style={{ marginTop: 8 }}>
      <InputLabel id={labelId}>Seleccionar Plantilla Pre-seteada</InputLabel>
      <Select labelId={labelId} value={selectedTemplateId} onChange={onSelect}>
        <MenuItem value="">
          <em>Ninguna</em>
        </MenuItem>
        {templates.map(template => (
          <MenuItem key={template.id} value={template.id}>
            {template.title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

const ReviewDetailDialog: React.FC<ReviewDetailDialogProps> = ({
  review,
  onClose,
  onReplied,
  canRespond = true,
}) => {
  const [googleReplyText, setGoogleReplyText] = useState(review.googleReplyText || '')
  const [nativeReplyText, setNativeReplyText] = useState('')
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('')
  const [submitting, setSubmitting] = useState(false)

  const isGoogle = review.source === 'google'

  const [{ data: replyTemplates }] = useApi<GetReplyTemplatesResponse, ReviewReplyTemplate[]>(
    () => getReplyTemplates(false),
    getReplyTemplatesResponseGetter,
    {
      baseData: [],
      deps: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )

  const displayDate =
    isGoogle && review.googleReviewTime ? review.googleReviewTime : review.createdAt

  const userLabel = getUserLabel(review)

  const handleGoogleReply = async () => {
    if (!googleReplyText.trim()) return
    setSubmitting(true)
    try {
      await api.request(replyToGoogleReview(review.id, googleReplyText.trim()))
      snackbar.show('Respuesta publicada en Google')
      onReplied?.()
      onClose()
    } catch {
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    } finally {
      setSubmitting(false)
    }
  }

  const handleNativeReply = async () => {
    if (!nativeReplyText.trim()) return
    setSubmitting(true)
    try {
      await api.request(replyToNativeReview(review.id, nativeReplyText.trim()))
      snackbar.show('Respuesta enviada al usuario')
      onReplied?.()
      onClose()
    } catch {
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    } finally {
      setSubmitting(false)
    }
  }

  const handleTemplateSelect = (setReplyText: React.Dispatch<React.SetStateAction<string>>) => (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    const { value } = event.target
    if (value === '') {
      setSelectedTemplateId('')
      return
    }

    const templateId = Number(value)
    const template = replyTemplates.find(item => item.id === templateId)
    setSelectedTemplateId(templateId)
    if (template) {
      setReplyText(template.body)
    }
  }

  return (
    <Dialog title={`Review #${review.id}`} isOpen readOnly showActions onCancel={onClose}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="textSecondary">
            Fecha
          </Typography>
          <Typography variant="body1">{formatDate(displayDate)}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="textSecondary">
            Local
          </Typography>
          <Typography variant="body1">{review.store?.name ?? '—'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="textSecondary">
            {isGoogle ? 'Reseñador' : 'Usuario'}
          </Typography>
          <Typography variant="body1">{userLabel}</Typography>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Typography variant="caption" color="textSecondary">
            Estrellas
          </Typography>
          <Typography variant="body1">{review.stars}</Typography>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Typography variant="caption" color="textSecondary">
            Origen
          </Typography>
          <Typography variant="body1">{review.source}</Typography>
        </Grid>
        {!isGoogle && (
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              WhatsApp
            </Typography>
            <Typography variant="body1">{review.contactWhatsapp || '—'}</Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography variant="caption" color="textSecondary">
            Comentario
          </Typography>
          <Typography
            variant="body1"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 4 }}
          >
            {review.description?.trim() || 'Sin comentario'}
          </Typography>
        </Grid>
        {isGoogle && review.googleReplyText && (
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Respuesta publicada
            </Typography>
            <Typography
              variant="body1"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 4 }}
            >
              {review.googleReplyText}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatDate(review.googleReviewTime)}
            </Typography>
          </Grid>
        )}
        {isGoogle && !review.googleReplyText && canRespond && (
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Responder en Google
            </Typography>
            <ReplyTemplateSelect
              templates={replyTemplates}
              selectedTemplateId={selectedTemplateId}
              labelId="google-reply-template-select-label"
              onSelect={handleTemplateSelect(setGoogleReplyText)}
            />
            <TextField
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              value={googleReplyText}
              onChange={e => setGoogleReplyText(e.target.value)}
              placeholder="Escribí la respuesta del negocio..."
              style={{ marginTop: 8 }}
            />
            <Button
              variant="contained"
              color="primary"
              style={{ marginTop: 12 }}
              disabled={submitting || !googleReplyText.trim()}
              onClick={handleGoogleReply}
            >
              Publicar respuesta
            </Button>
          </Grid>
        )}
        {!isGoogle && review.replyText && (
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Respuesta enviada
            </Typography>
            <Typography
              variant="body1"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 4 }}
            >
              {review.replyText}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatDate(review.replyTime)}
            </Typography>
          </Grid>
        )}
        {!isGoogle && !review.replyText && canRespond && (
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Responder al usuario
            </Typography>
            <ReplyTemplateSelect
              templates={replyTemplates}
              selectedTemplateId={selectedTemplateId}
              labelId="native-reply-template-select-label"
              onSelect={handleTemplateSelect(setNativeReplyText)}
            />
            <TextField
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              value={nativeReplyText}
              onChange={e => setNativeReplyText(e.target.value)}
              placeholder="Escribí la respuesta oficial de la marca..."
              style={{ marginTop: 8 }}
            />
            <Button
              variant="contained"
              color="primary"
              style={{ marginTop: 12 }}
              disabled={submitting || !nativeReplyText.trim()}
              onClick={handleNativeReply}
            >
              Enviar Respuesta
            </Button>
          </Grid>
        )}
      </Grid>
    </Dialog>
  )
}

ReviewDetailDialog.defaultProps = {
  onReplied: () => undefined,
  canRespond: true,
}

export { ReviewDetailDialog }
